import * as React from 'react'
import {
  getPrice,
  getStatus,
  createCheckout,
  loginPrivate,
  readTokenFromHash,
  readEmailFromToken,
  isTokenExpired,
  type MobilePrice,
  type MobileInvoiceBuyer,
} from '../api'
import { clearMobileToken, readMobileToken, storeMobileToken } from '../config'
import { LegalFooter, TermsAcceptance } from '@/modules/legal'

const card: React.CSSProperties = { maxWidth: 460, margin: '64px auto', padding: 24, textAlign: 'center' }
const primaryBtn: React.CSSProperties = {
  display: 'inline-block', marginTop: 24, padding: '12px 24px', border: 'none',
  background: '#2563eb', color: '#fff', borderRadius: 6, fontWeight: 600, cursor: 'pointer', width: '100%',
}
const input: React.CSSProperties = {
  width: '100%', padding: '10px 12px', marginTop: 8, border: '1px solid #d1d5db', borderRadius: 6,
}
const field: React.CSSProperties = { display: 'block', marginTop: 12 }
const row: React.CSSProperties = { display: 'flex', gap: 12 }

const emptyBuyer: MobileInvoiceBuyer = {
  name: '', street: '', buildingNo: '', apartmentNo: '', postcode: '', city: '', country: 'PL', email: '',
}

// Mirrors the backend's INVOICE_DETAILS_REQUIRED check: everything except the
// apartment number is mandatory (a flat number is genuinely absent for houses).
function isBuyerComplete(b: MobileInvoiceBuyer): boolean {
  return [b.name, b.street, b.buildingNo, b.postcode, b.city, b.email].every((v) => v.trim() !== '')
}

const SESSION_EXPIRED_MESSAGE =
  'Sesja wygasła. Zaloguj się ponownie albo wróć do aplikacji i kliknij „Zapłać” jeszcze raz. Wpisane dane do faktury zostaną zachowane.'

function formatPrice(p: MobilePrice): string {
  const major = (p.priceGrosze / 100).toFixed(2).replace('.', ',')
  return `${major} ${p.currency === 'PLN' ? 'zł' : p.currency}`
}

export const MobileCheckoutPage: React.FC = () => {
  const [token, setToken] = React.useState<string | null>(null)
  const [price, setPrice] = React.useState<MobilePrice | null>(null)
  const [alreadyPaid, setAlreadyPaid] = React.useState(false)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  // login form state (fallback when no token was forwarded by the app)
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')

  // Invoice details — required before paying, since P24 gives us no buyer data.
  const [buyer, setBuyer] = React.useState<MobileInvoiceBuyer>(emptyBuyer)
  const [termsAccepted, setTermsAccepted] = React.useState(false)
  const setBuyerField = (key: keyof MobileInvoiceBuyer) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setBuyer((prev) => ({ ...prev, [key]: e.target.value }))

  // Deep link back to the native app — present in the URL only when opened from
  // the mobile app. Forwarded to the backend so it's carried into the P24
  // urlReturn → the success page shows the "return to app" link exclusively for
  // app-initiated payments (web payments never get it).
  const returnUrlRef = React.useRef<string | null>(
    new URLSearchParams(window.location.search).get('return_url'),
  )

  // On mount: take a token forwarded by the app (URL hash) or a stored one.
  React.useEffect(() => {
    const fromHash = readTokenFromHash()
    const t = fromHash ?? readMobileToken()
    if (fromHash) {
      storeMobileToken(fromHash)
      // strip the token from the address bar
      window.history.replaceState(null, '', window.location.pathname + window.location.search)
    }
    if (t) setToken(t)
    getPrice().then(setPrice).catch(() => setError('Nie udało się pobrać ceny.'))
  }, [])

  // When we have a token, check whether the user already paid.
  React.useEffect(() => {
    if (!token) return
    getStatus(token)
      .then((s) => setAlreadyPaid(s.paid))
      .catch(() => {
        /* invalid/expired token — drop it so the login form shows */
        setToken(null)
      })
  }, [token])

  // Prefill the invoice email from the token, leaving it editable — the invoice
  // may well belong somewhere other than the login address.
  React.useEffect(() => {
    if (!token) return
    const claimed = readEmailFromToken(token)
    if (claimed) setBuyer((prev) => (prev.email ? prev : { ...prev, email: claimed }))
  }, [token])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      const t = await loginPrivate(email.trim(), password)
      storeMobileToken(t)
      setToken(t)
    } catch {
      setError('Logowanie nie powiodło się. Sprawdź e-mail i hasło.')
    } finally {
      setBusy(false)
    }
  }

  // Drop the expired token so the login form shows; the invoice details and the
  // terms checkbox live in page state and survive the re-login.
  const expireSession = (expiredToken: string) => {
    clearMobileToken()
    setToken(null)
    setEmail((prev) => prev || readEmailFromToken(expiredToken) || '')
    setError(SESSION_EXPIRED_MESSAGE)
  }

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token || !termsAccepted) return
    if (isTokenExpired(token)) {
      expireSession(token)
      return
    }
    setError(null)
    setBusy(true)
    try {
      const res = await createCheckout(token, buyer, returnUrlRef.current)
      window.location.href = res.url
    } catch (err: unknown) {
      const response = (err as { response?: { status?: number; data?: { code?: string } } })?.response
      const code = response?.data?.code
      if (response?.status === 401) {
        // Expired despite the check above (clock skew) or revoked server-side.
        expireSession(token)
      } else if (code === 'ALREADY_PAID') {
        setAlreadyPaid(true)
      } else if (code === 'PRICE_NOT_CONFIGURED') {
        setError('Cena nie jest jeszcze skonfigurowana.')
      } else if (code === 'INVOICE_DETAILS_REQUIRED') {
        setError('Uzupełnij dane do faktury.')
      } else {
        setError('Nie udało się rozpocząć płatności. Spróbuj ponownie.')
      }
      setBusy(false)
    }
  }

  if (alreadyPaid) {
    return (
      <Shell>
        <div style={{ fontSize: 48, marginBottom: 16 }}>✓</div>
        <h1>Masz już dostęp</h1>
        <p style={{ color: '#6b7280', marginTop: 8 }}>
          Twoje konto aplikacji mobilnej jest opłacone. Wróć do aplikacji EasyTacho.
        </p>
      </Shell>
    )
  }

  return (
    <Shell>
      <h1>Aplikacja mobilna EasyTacho</h1>
      <p style={{ color: '#6b7280', marginTop: 8 }}>
        Aby korzystać z aplikacji, dokończ jednorazową płatność
        {price ? <> w kwocie <strong>{formatPrice(price)}</strong></> : null} przez Przelewy24.
      </p>

      {error && <p style={{ color: '#dc2626', marginTop: 12 }}>{error}</p>}

      {!token ? (
        <form onSubmit={handleLogin} style={{ marginTop: 24, textAlign: 'left' }}>
          <label>
            E-mail
            <input style={input} type="email" autoComplete="username" value={email}
              onChange={(e) => setEmail(e.target.value)} required />
          </label>
          <label style={{ display: 'block', marginTop: 12 }}>
            Hasło
            <input style={input} type="password" autoComplete="current-password" value={password}
              onChange={(e) => setPassword(e.target.value)} required />
          </label>
          <button style={primaryBtn} type="submit" disabled={busy}>
            {busy ? 'Logowanie…' : 'Zaloguj się'}
          </button>
        </form>
      ) : (
        <form onSubmit={handlePay} style={{ marginTop: 24, textAlign: 'left' }}>
          <h2 style={{ fontSize: 16, margin: 0 }}>Dane do faktury</h2>
          <p style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>
            Fakturę wyślemy w formacie PDF na podany adres e-mail.
          </p>

          <label style={field}>
            Imię i nazwisko
            <input style={input} autoComplete="name" value={buyer.name}
              onChange={setBuyerField('name')} required />
          </label>

          <label style={field}>
            Ulica
            <input style={input} autoComplete="address-line1" value={buyer.street}
              onChange={setBuyerField('street')} required />
          </label>

          <div style={row}>
            <label style={{ ...field, flex: 1 }}>
              Nr budynku
              <input style={input} value={buyer.buildingNo}
                onChange={setBuyerField('buildingNo')} required />
            </label>
            <label style={{ ...field, flex: 1 }}>
              Nr lokalu
              <input style={input} value={buyer.apartmentNo}
                onChange={setBuyerField('apartmentNo')} placeholder="opcjonalnie" />
            </label>
          </div>

          <div style={row}>
            <label style={{ ...field, flex: 1 }}>
              Kod pocztowy
              <input style={input} autoComplete="postal-code" value={buyer.postcode}
                onChange={setBuyerField('postcode')} placeholder="00-001" required />
            </label>
            <label style={{ ...field, flex: 2 }}>
              Miejscowość
              <input style={input} autoComplete="address-level2" value={buyer.city}
                onChange={setBuyerField('city')} required />
            </label>
          </div>

          <label style={field}>
            E-mail do faktury
            <input style={input} type="email" autoComplete="email" value={buyer.email}
              onChange={setBuyerField('email')} required />
          </label>

          <TermsAcceptance
            checked={termsAccepted}
            onChange={setTermsAccepted}
            id="mobile-accept-terms"
            style={{ marginTop: 20 }}
          />
          <p style={{ color: '#6b7280', fontSize: 12, marginTop: 8 }}>
            Dostęp aktywujemy niezwłocznie po potwierdzeniu płatności przez Przelewy24,
            nie później niż w ciągu 24 godzin od jej zaksięgowania. Zasady odstąpienia
            od umowy opisuje Regulamin.
          </p>

          <button
            style={primaryBtn}
            type="submit"
            disabled={busy || !price || !isBuyerComplete(buyer) || !termsAccepted}
          >
            {busy ? 'Przekierowanie…' : `Zapłać${price ? ` ${formatPrice(price)}` : ''}`}
          </button>
        </form>
      )}
    </Shell>
  )
}

// Karta zakupu ze stopką zawierającą dane kontaktowe usługodawcy i linki do
// dokumentów — ta ścieżka jest poza AppLayoutNoCompany (własna autoryzacja).
const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
    <div style={{ flex: 1 }}>
      <div style={card}>{children}</div>
    </div>
    <LegalFooter />
  </div>
)
